import type { NetworkPlayer, NetworkVector } from '../network/protocol';

const fullTurn = Math.PI*2;

export function incomingBearing(local: NetworkPlayer, source: NetworkPlayer): number {
  const deltaX = source.position.x-local.position.x;
  const deltaZ = source.position.z-local.position.z;
  const worldBearing = Math.atan2(deltaX, -deltaZ);
  let relative = (worldBearing+local.yaw)%fullTurn;
  if (relative > Math.PI) relative -= fullTurn;
  if (relative < -Math.PI) relative += fullTurn;
  return relative;
}

export class CombatFeedback {
  private readonly crosshair: HTMLElement;
  private readonly damage: HTMLElement;
  private crosshairTimer = 0;
  private damageTimer = 0;

  public constructor(root: ParentNode) {
    const crosshair = root.querySelector<HTMLElement>('[data-crosshair]');
    const damage = root.querySelector<HTMLElement>('[data-damage-direction]');
    if (!crosshair || !damage) throw new Error('Missing combat feedback HUD');
    this.crosshair = crosshair;
    this.damage = damage;
  }

  public confirmed(): void {
    this.pulseCrosshair('is-hit', 190);
  }

  public blocked(): void {
    this.pulseCrosshair('is-blocked', 230);
  }

  public incoming(
    players: readonly NetworkPlayer[],
    localPlayerID: string,
    sourceID?: string,
    impactPosition?: NetworkVector,
  ): void {
    const local = players.find((player) => player.id === localPlayerID);
    const source = players.find((player) => player.id === sourceID);
    if (!local || !source) return;
    const bearingOrigin = impactPosition ? { ...local, position: impactPosition } : local;
    const bearing = incomingBearing(bearingOrigin, source);
    this.damage.style.setProperty('--damage-bearing', `${bearing}rad`);
    this.damage.style.setProperty('--damage-label-bearing', `${-bearing}rad`);
    this.damage.dataset.feedback = 'incoming';
    this.damage.dataset.sourceId = source.id;
    this.damage.classList.remove('is-visible');
    void this.damage.offsetWidth;
    this.damage.classList.add('is-visible');
    window.clearTimeout(this.damageTimer);
    this.damageTimer = window.setTimeout(() => this.damage.classList.remove('is-visible'), 720);
  }

  public clear(): void {
    window.clearTimeout(this.crosshairTimer);
    window.clearTimeout(this.damageTimer);
    this.crosshairTimer = 0;
    this.damageTimer = 0;
    this.crosshair.classList.remove('is-hit', 'is-blocked');
    this.damage.classList.remove('is-visible');
    delete this.crosshair.dataset.feedback;
    delete this.damage.dataset.feedback;
    delete this.damage.dataset.sourceId;
  }

  private pulseCrosshair(className: 'is-hit' | 'is-blocked', duration: number): void {
    window.clearTimeout(this.crosshairTimer);
    this.crosshair.classList.remove('is-hit', 'is-blocked');
    void this.crosshair.offsetWidth;
    this.crosshair.classList.add(className);
    this.crosshair.dataset.feedback = className === 'is-hit' ? 'hit' : 'blocked';
    this.crosshairTimer = window.setTimeout(() => this.crosshair.classList.remove(className), duration);
  }
}
